import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Shield, Bell, Users, Loader2, Mail, Lock, User,
  ArrowLeft, Sparkles, Globe, CheckCircle2
} from "lucide-react";
import euSearchIcon from "@/assets/eu-search-icon.png";
import { supabase } from "@/integrations/supabase/client";
import { SUBSCRIPTION_TIERS, TierKey } from "@/lib/subscriptions";
import SEOHead from "@/components/SEOHead";
import { useLocationDetection } from "@/hooks/useLocationDetection";

export default function Auth() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const { user, signIn, signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const { isLoading: locationLoading } = useLocationDetection();

  const planParam = searchParams.get("plan") as TierKey | null;
  const selectedPlan = planParam && SUBSCRIPTION_TIERS[planParam] ? SUBSCRIPTION_TIERS[planParam] : null;
  const countryParam = (searchParams.get("country") || "IT").toUpperCase();
  const hasTriggeredCheckoutRef = useRef(false);

  const handleCheckout = useCallback(async () => {
    if (!selectedPlan) {
      navigate("/dashboard");
      return;
    }

    setCheckoutLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId: selectedPlan.priceId, country: countryParam },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      } else {
        navigate("/dashboard");
      }
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast({
        variant: "destructive",
        title: t("auth.checkoutError"),
        description: error.message || t("auth.checkoutErrorMessage"),
      });
      navigate("/dashboard");
    } finally {
      setCheckoutLoading(false);
    }
  }, [selectedPlan, countryParam, navigate, toast, t]);

  useEffect(() => {
    if (!user) return;

    if (planParam && selectedPlan) {
      if (hasTriggeredCheckoutRef.current) return;
      hasTriggeredCheckoutRef.current = true;
      handleCheckout();
      return;
    }

    navigate("/dashboard");
  }, [user, planParam, selectedPlan, handleCheckout, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const { error } = await signIn(email, password);

    if (error) {
      toast({
        variant: "destructive",
        title: t("auth.loginError"),
        description: error.message === "Invalid login credentials" ? t("auth.invalidCredentials") : error.message,
      });
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (password.length < 6) {
      toast({
        variant: "destructive",
        title: t("auth.passwordTooShort"),
        description: t("auth.passwordTooShort"),
      });
      setIsLoading(false);
      return;
    }

    const { error } = await signUp(email, password, fullName);

    if (error) {
      let message = error.message;
      if (error.message.includes("already registered")) {
        message = t("auth.alreadyRegistered");
      } else if (
        error.message.includes("password") ||
        error.message.includes("422") ||
        error.message.includes("breached") ||
        error.message.includes("pwned")
      ) {
        message = t("auth.passwordCompromised");
      }

      toast({
        variant: "destructive",
        title: t("auth.registerError"),
        description: message,
      });
      setIsLoading(false);
    } else {
      // Admin notification for new registrations is sent server-side by the
      // auth email hook, so the browser no longer calls notify-admin directly.
      toast({
        title: t("auth.registerSuccess"),
        description: t("auth.checkEmailForConfirmation"),
      });
      setIsLoading(false);
    }
  };

  if (locationLoading || checkoutLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">
            {checkoutLoading ? t("auth.redirectingToCheckout") : t("common.loading")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead title={t("auth.login")} description={t("auth.platformDescription")} path="/auth" noIndex={true} />

      <div className="min-h-screen bg-background flex">
        {/* Left panel - Branding */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col justify-between p-12"
          style={{ background: 'var(--gradient-sidebar)' }}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, hsl(48 100% 50%), transparent)' }}
          />
          <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-5"
            style={{ background: 'radial-gradient(circle, hsl(48 100% 50%), transparent)' }}
          />

          {/* Header with logo and home link */}
          <div className="relative z-10">
            <Link to="/" className="inline-flex items-center gap-3 mb-2 group transition-opacity hover:opacity-80">
              <img src="/logo-mark.png" alt="TED Monitor" className="w-12 h-12 rounded-xl object-contain" />
              <h1 className="text-2xl font-bold text-sidebar-foreground">TED Monitor</h1>
            </Link>
            <p className="text-sidebar-foreground/70 text-lg max-w-md mt-3">
              {t("auth.platformDescription")}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-4 text-sm text-sidebar-foreground/80 hover:text-sidebar-foreground transition-colors font-medium underline underline-offset-4 decoration-sidebar-foreground/40 hover:decoration-sidebar-foreground/70"
            >
              <ArrowLeft className="w-4 h-4" />
              {t("auth.backToHome")}
            </Link>
          </div>

          {/* Features */}
          <div className="relative z-10 space-y-6">
            <div className="flex items-start gap-4 group">
              <div className="w-11 h-11 rounded-xl bg-sidebar-accent/80 flex items-center justify-center shrink-0 overflow-hidden shadow-lg shadow-black/10 group-hover:scale-105 transition-transform">
                <img src={euSearchIcon} alt="EU Search" className="w-7 h-7 object-contain" />
              </div>
              <div>
                <h3 className="font-semibold text-sidebar-foreground">{t("auth.advancedSearch")}</h3>
                <p className="text-sm text-sidebar-foreground/60">{t("auth.advancedSearchDesc")}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 group">
              <div className="w-11 h-11 rounded-xl bg-sidebar-accent/80 flex items-center justify-center shrink-0 shadow-lg shadow-black/10 group-hover:scale-105 transition-transform">
                <Bell className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="font-semibold text-sidebar-foreground">{t("auth.automaticAlerts")}</h3>
                <p className="text-sm text-sidebar-foreground/60">{t("auth.automaticAlertsDesc")}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 group">
              <div className="w-11 h-11 rounded-xl bg-sidebar-accent/80 flex items-center justify-center shrink-0 shadow-lg shadow-black/10 group-hover:scale-105 transition-transform">
                <Sparkles className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="font-semibold text-sidebar-foreground">{t("auth.multiUser")}</h3>
                <p className="text-sm text-sidebar-foreground/60">{t("auth.multiUserDesc")}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 group">
              <div className="w-11 h-11 rounded-xl bg-sidebar-accent/80 flex items-center justify-center shrink-0 shadow-lg shadow-black/10 group-hover:scale-105 transition-transform">
                <Shield className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="font-semibold text-sidebar-foreground">{t("auth.secureReliable")}</h3>
                <p className="text-sm text-sidebar-foreground/60">{t("auth.secureReliableDesc")}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 group">
              <div className="w-11 h-11 rounded-xl bg-sidebar-accent/80 flex items-center justify-center shrink-0 shadow-lg shadow-black/10 group-hover:scale-105 transition-transform">
                <Globe className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="font-semibold text-sidebar-foreground">{t("auth.euCoverage")}</h3>
                <p className="text-sm text-sidebar-foreground/60">27 EU {t("auth.countries")}</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="relative z-10 flex items-center justify-between">
            <p className="text-sm text-sidebar-foreground/40">© 2025 TED Monitor. {t("auth.allRightsReserved")}</p>
            <div className="flex items-center gap-1 text-xs text-sidebar-foreground/30">
              <Shield className="w-3 h-3" />
              SSL / GDPR
            </div>
          </div>
        </div>

        {/* Right panel - Auth forms */}
        <div className="flex-1 flex flex-col">
          {/* Mobile home link */}
          <div className="lg:hidden flex items-center justify-between p-4 border-b border-border">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              {t("auth.backToHome")}
            </Link>
          </div>

          <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
            <Card className="w-full max-w-md animate-fade-in shadow-xl border-border/50">
              <CardHeader className="text-center pb-2">
                <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
                  <img src="/logo-mark.png" alt="TED Monitor" className="w-10 h-10 rounded-lg object-contain" />
                  <span className="text-xl font-bold">TED Monitor</span>
                </div>

                <CardTitle className="text-2xl">{t("auth.loginTitle")}</CardTitle>
                <CardDescription className="mt-1">
                  {selectedPlan ? t("auth.loginDescriptionWithPlan") : t("auth.loginDescription")}
                </CardDescription>

                {selectedPlan && (
                  <div className="mt-4 p-3 bg-primary/10 rounded-lg border border-primary/20">
                    <div className="flex items-center justify-between">
                      <div className="text-left">
                        <div className="flex items-center gap-1.5">
                          <CheckCircle2 className="w-4 h-4 text-primary" />
                          <p className="text-sm font-medium text-foreground">{selectedPlan.name}</p>
                        </div>
                        <p className="text-xs text-muted-foreground ml-5.5">
                          {selectedPlan.users} {selectedPlan.users > 1 ? t("auth.users") : t("auth.user")} •
                          {selectedPlan.scope === "european"
                            ? ` ${t("auth.euCoverage")}`
                            : ` ${t("auth.nationalCoverage")}`}
                        </p>
                      </div>
                      <Badge variant="secondary" className="text-lg font-bold">
                        €{selectedPlan.price}
                        {t("auth.perMonth")}
                      </Badge>
                    </div>
                  </div>
                )}
              </CardHeader>

              <CardContent className="pt-4">
                <Tabs defaultValue="login" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="login">{t("auth.login")}</TabsTrigger>
                    <TabsTrigger value="register">{t("auth.register")}</TabsTrigger>
                  </TabsList>

                  <TabsContent value="login">
                    <form onSubmit={handleSignIn} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">{t("auth.email")}</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="email"
                            type="email"
                            placeholder={t("auth.emailPlaceholder")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="pl-10"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="password">{t("auth.password")}</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="password"
                            type="password"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="pl-10"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                          {t("auth.forgotPassword")}
                        </Link>
                      </div>

                      <Button type="submit" className="w-full h-11 text-base font-medium" disabled={isLoading}>
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            {t("auth.loggingIn")}
                          </>
                        ) : selectedPlan ? (
                          t("auth.loginAndCheckout")
                        ) : (
                          t("auth.login")
                        )}
                      </Button>
                    </form>
                  </TabsContent>

                  <TabsContent value="register">
                    <form onSubmit={handleSignUp} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="fullName">{t("auth.fullName")}</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="fullName"
                            type="text"
                            placeholder={t("auth.fullNamePlaceholder")}
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            required
                            className="pl-10"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="emailRegister">{t("auth.email")}</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="emailRegister"
                            type="email"
                            placeholder={t("auth.emailPlaceholder")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="pl-10"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="passwordRegister">{t("auth.password")}</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="passwordRegister"
                            type="password"
                            placeholder={t("auth.passwordPlaceholder")}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={6}
                            className="pl-10"
                          />
                        </div>
                      </div>

                      <Button type="submit" className="w-full h-11 text-base font-medium" disabled={isLoading}>
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            {t("auth.registering")}
                          </>
                        ) : selectedPlan ? (
                          t("auth.registerAndCheckout")
                        ) : (
                          t("auth.createAccount")
                        )}
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>

                {/* Footer links */}
                <div className="mt-6 pt-4 border-t border-border text-center">
                  <p className="text-xs text-muted-foreground">
                    {t("auth.agreeToTerms")}{" "}
                    <Link to="/terms-conditions" className="text-primary hover:underline">{t("footer.termsConditions")}</Link>
                    {" & "}
                    <Link to="/privacy-policy" className="text-primary hover:underline">{t("footer.privacyPolicy")}</Link>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
